// Interactive ID Photo Canvas Engine

import { segmentImage } from './segmenter';

export interface CropState {
  x: number;          // horizontal offset (fraction of crop window width)
  y: number;          // vertical offset (fraction of crop window height)
  scale: number;      // scale factor (1.0 = image width matches crop window width)
  rotation: number;   // rotation in radians
  flipped: boolean;   // horizontal flip state
}

export interface ImageFilters {
  brightness: number;  // 50 - 150 (100 is default)
  contrast: number;    // 50 - 150 (100 is default)
  saturation: number;  // 0 - 200 (100 is default)
  smooth: number;      // 0 - 10 (skin smoothing intensity)
  sharpen: number;     // 0 - 5 (sharpen intensity)
  grayscale: boolean;  // black & white toggle
}

export interface ChromaKeySettings {
  color: [number, number, number] | null; // RGB to replace
  tolerance: number;  // 5 - 100
  feather: number;    // 0 - 10 px
}

export class IDPhotoCanvas {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private viewport: HTMLElement;
  
  // Image & States
  private sourceImage: HTMLImageElement | null = null;
  private cropState: CropState = { x: 0, y: 0, scale: 1.0, rotation: 0, flipped: false };
  private filters: ImageFilters = { brightness: 100, contrast: 100, saturation: 100, smooth: 0, sharpen: 0, grayscale: false };
  
  // Dimensions & Configuration
  private targetSize = { widthMm: 35, heightMm: 45 }; // Default 35x45mm (Schengen/2-inch)
  private dpi = 300;
  private showGuides = true;
  private isEditMode = true; // true = Crop & Edit, false = Layout preview
  
  // Background replacement
  private bgType: 'solid' | 'gradient' | 'transparent' = 'solid';
  private bgSolidColor = '#ffffff';
  private bgGradientVignette = false;
  private useAICutout = false;
  private aiMaskCanvas: HTMLCanvasElement | null = null;
  private chromaKey: ChromaKeySettings = { color: null, tolerance: 30, feather: 2 };
  
  // Interaction variables
  private isGrabbing = false;
  private startDragX = 0;
  private startDragY = 0;
  private zoomLevel = 1.0; // Viewport display zoom (visual only)

  // Callbacks
  public onStateChange: (() => void) | null = null;
  public onToast: ((msg: string) => void) | null = null;

  constructor(canvasId: string, viewportId: string) {
    this.canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    const context = this.canvas.getContext('2d');
    if (!context) throw new Error('Could not get 2D canvas context');
    this.ctx = context;
    this.viewport = document.getElementById(viewportId) as HTMLElement;

    this.setupEventListeners();
  }

  // Setters & Actions
  public setImage(img: HTMLImageElement) {
    this.sourceImage = img;
    this.aiMaskCanvas = null; // Reset AI mask for new image
    this.resetCrop();
  }

  public getSourceImage() {
    return this.sourceImage;
  }

  public setTargetSize(widthMm: number, heightMm: number) {
    this.targetSize = { widthMm, heightMm };
    this.update();
  }

  public getTargetSize() {
    return this.targetSize;
  }

  public setGuidesVisible(visible: boolean) {
    this.showGuides = visible;
    this.update();
  }

  public setMode(editMode: boolean) {
    this.isEditMode = editMode;
    this.update();
  }

  public setFilters(newFilters: Partial<ImageFilters>) {
    this.filters = { ...this.filters, ...newFilters };
    this.update();
  }

  public resetFilters() {
    this.filters = { brightness: 100, contrast: 100, saturation: 100, smooth: 0, sharpen: 0, grayscale: false };
    this.update();
  }

  public setBGColor(color: string) {
    if (color === 'transparent') {
      this.bgType = 'transparent';
    } else {
      this.bgType = 'solid';
      this.bgSolidColor = color;
    }
    this.update();
  }

  public setBGVignette(active: boolean) {
    this.bgGradientVignette = active;
    this.update();
  }

  public setChromaKeyColor(rgb: [number, number, number] | null) {
    this.chromaKey.color = rgb;
    this.update();
  }

  public setChromaKeySettings(settings: Partial<ChromaKeySettings>) {
    this.chromaKey = { ...this.chromaKey, ...settings };
    this.update();
  }

  public setAICutout(active: boolean, onLoaded?: () => void) {
    this.useAICutout = active;
    if (active && this.sourceImage && !this.aiMaskCanvas) {
      if (this.onToast) this.onToast('Running AI segmentation...');
      segmentImage(this.sourceImage, () => {
        if (onLoaded) onLoaded();
      }).then(mask => {
        this.aiMaskCanvas = mask;
        if (this.onToast) this.onToast('AI cutout applied successfully.');
        this.update();
      }).catch(err => {
        console.error(err);
        this.useAICutout = false;
        if (this.onToast) this.onToast('AI Segmentation failed: ' + err.message);
        this.update();
      });
    } else {
      this.update();
    }
  }

  public isAICutoutActive() {
    return this.useAICutout;
  }

  public rotateCCW() {
    this.cropState.rotation -= Math.PI / 2;
    this.update();
  }

  public rotateCW() {
    this.cropState.rotation += Math.PI / 2;
    this.update();
  }

  public setFineRotation(deg: number) {
    // Keep 90-degree components, adjust the remainder
    const baseRot = Math.round(this.cropState.rotation / (Math.PI / 2)) * (Math.PI / 2);
    const rad = (deg * Math.PI) / 180;
    this.cropState.rotation = baseRot + rad;
    this.update();
  }

  public flipHorizontal() {
    this.cropState.flipped = !this.cropState.flipped;
    this.update();
  }

  public resetCrop() {
    if (!this.sourceImage) return;
    this.cropState = {
      x: 0,
      y: 0,
      scale: 1.0,
      rotation: 0,
      flipped: false
    };
    
    // Auto scale to fill crop window width
    this.cropState.scale = 1.15;
    this.zoomLevel = 1.0;
    this.update();
  }

  public updateZoom(factor: number) {
    this.zoomLevel = Math.max(0.2, Math.min(4.0, this.zoomLevel * factor));
    this.applyZoom();
  }

  public setZoom(level: number) {
    this.zoomLevel = level;
    this.applyZoom();
  }

  public getZoom() {
    return this.zoomLevel;
  }

  private applyZoom() {
    this.canvas.style.transform = `scale(${this.zoomLevel})`;
    if (this.onStateChange) this.onStateChange();
  }

  public resetZoom() {
    // Calculate fit-to-screen scale
    if (!this.viewport) return;
    const viewW = this.viewport.clientWidth - 48;
    const viewH = this.viewport.clientHeight - 48;
    const canvasW = this.canvas.width;
    const canvasH = this.canvas.height;
    
    const scaleX = viewW / canvasW;
    const scaleY = viewH / canvasH;
    this.zoomLevel = Math.min(1.0, Math.min(scaleX, scaleY));
    this.applyZoom();
  }

  // Draw Operations
  public update() {
    if (!this.sourceImage || !this.isEditMode) return;

    const canvasWidth = 600;
    const canvasHeight = 650;
    this.canvas.width = canvasWidth;
    this.canvas.height = canvasHeight;

    // Draw background
    this.ctx.fillStyle = '#1e293b'; // Slate background of viewport
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Calculate crop window box
    const margin = 50;
    const maxCropW = canvasWidth - margin * 2;
    const maxCropH = canvasHeight - margin * 2;

    const aspect = this.targetSize.widthMm / this.targetSize.heightMm;
    
    let cropW = maxCropW;
    let cropH = cropW / aspect;
    if (cropH > maxCropH) {
      cropH = maxCropH;
      cropW = cropH * aspect;
    }

    const cropX = (canvasWidth - cropW) / 2;
    const cropY = (canvasHeight - cropH) / 2;

    // Draw the image transformed behind the crop window
    this.ctx.save();
    
    // Set clipping path to the crop window so the image is visually clipped (optional, but standard croppers show outside region dimmed)
    // We will draw the full image, then draw a dark semi-transparent mask on top except for the crop window.
    this.ctx.save();
    // Translate to center of crop window
    this.ctx.translate(cropX + cropW / 2 + this.cropState.x * cropW, cropY + cropH / 2 + this.cropState.y * cropH);
    this.ctx.rotate(this.cropState.rotation);
    if (this.cropState.flipped) {
      this.ctx.scale(-1, 1);
    }
    
    // Scale where scale = 1.0 means image width fits crop window width
    const baseScale = cropW / this.sourceImage.width;
    this.ctx.scale(baseScale * this.cropState.scale, baseScale * this.cropState.scale);

    // Draw original image
    this.ctx.drawImage(this.sourceImage, -this.sourceImage.width / 2, -this.sourceImage.height / 2);
    this.ctx.restore();

    // Draw the outer dark mask overlay
    this.ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
    
    // Top box
    this.ctx.fillRect(0, 0, canvasWidth, cropY);
    // Bottom box
    this.ctx.fillRect(0, cropY + cropH, canvasWidth, canvasHeight - (cropY + cropH));
    // Left box
    this.ctx.fillRect(0, cropY, cropX, cropH);
    // Right box
    this.ctx.fillRect(cropX + cropW, cropY, canvasWidth - (cropX + cropW), cropH);

    // Draw crop window borders
    this.ctx.strokeStyle = '#6366f1';
    this.ctx.lineWidth = 2;
    this.ctx.strokeRect(cropX, cropY, cropW, cropH);

    // Draw guides
    if (this.showGuides) {
      this.ctx.save();
      this.ctx.strokeStyle = 'rgba(255, 255, 255, 0.4)';
      this.ctx.lineWidth = 1;
      this.ctx.setLineDash([4, 4]);

      // Vertical center line
      this.ctx.beginPath();
      this.ctx.moveTo(cropX + cropW / 2, cropY);
      this.ctx.lineTo(cropX + cropW / 2, cropY + cropH);
      this.ctx.stroke();

      // Eye height guideline (typically 38-40% from top of crop window)
      const eyeY = cropY + cropH * 0.38;
      this.ctx.beginPath();
      this.ctx.moveTo(cropX, eyeY);
      this.ctx.lineTo(cropX + cropW, eyeY);
      this.ctx.stroke();

      // Head placement oval outline
      this.ctx.beginPath();
      this.ctx.ellipse(
        cropX + cropW / 2, 
        cropY + cropH * 0.45, // oval center vertical
        cropW * 0.30,        // radius X
        cropH * 0.30,        // radius Y
        0, 0, Math.PI * 2
      );
      this.ctx.stroke();
      
      this.ctx.restore();
    }

    if (this.onStateChange) this.onStateChange();
  }

  /**
   * Generates a high-resolution, cropped, background-replaced, and color-filtered offscreen canvas of the single ID photo.
   */
  public generateCroppedIDCanvas(): HTMLCanvasElement {
    const targetW = Math.round((this.targetSize.widthMm / 25.4) * this.dpi);
    const targetH = Math.round((this.targetSize.heightMm / 25.4) * this.dpi);

    const outCanvas = document.createElement('canvas');
    outCanvas.width = targetW;
    outCanvas.height = targetH;
    const outCtx = outCanvas.getContext('2d');
    if (!outCtx) return outCanvas;

    if (!this.sourceImage) return outCanvas;

    // 1. Draw the image with transforms onto offscreen canvas
    outCtx.save();
    outCtx.translate(targetW / 2 + this.cropState.x * targetW, targetH / 2 + this.cropState.y * targetH);
    outCtx.rotate(this.cropState.rotation);
    if (this.cropState.flipped) {
      outCtx.scale(-1, 1);
    }
    const baseScale = targetW / this.sourceImage.width;
    outCtx.scale(baseScale * this.cropState.scale, baseScale * this.cropState.scale);
    outCtx.drawImage(this.sourceImage, -this.sourceImage.width / 2, -this.sourceImage.height / 2);
    outCtx.restore();

    // 2. Perform background removal and coloring
    // Create temporary canvas to hold original pixels for color manipulation
    const imgData = outCtx.getImageData(0, 0, targetW, targetH);
    const data = imgData.data;

    let maskData: Uint8ClampedArray | null = null;
    
    // Case A: AI Cutout mask is available
    if (this.useAICutout && this.aiMaskCanvas) {
      const tempMaskCanvas = document.createElement('canvas');
      tempMaskCanvas.width = targetW;
      tempMaskCanvas.height = targetH;
      const tempMaskCtx = tempMaskCanvas.getContext('2d');
      if (tempMaskCtx) {
        tempMaskCtx.save();
        tempMaskCtx.translate(targetW / 2 + this.cropState.x * targetW, targetH / 2 + this.cropState.y * targetH);
        tempMaskCtx.rotate(this.cropState.rotation);
        if (this.cropState.flipped) {
          tempMaskCtx.scale(-1, 1);
        }
        const mBaseScale = targetW / this.aiMaskCanvas.width;
        tempMaskCtx.scale(mBaseScale * this.cropState.scale, mBaseScale * this.cropState.scale);
        tempMaskCtx.drawImage(this.aiMaskCanvas, -this.aiMaskCanvas.width / 2, -this.aiMaskCanvas.height / 2);
        tempMaskCtx.restore();

        const maskImgData = tempMaskCtx.getImageData(0, 0, targetW, targetH);
        maskData = maskImgData.data;
      }
    }

    // Apply Background & Color changes
    const chromaRGB = this.chromaKey.color;
    const chromaTol = this.chromaKey.tolerance;
    const chromaFeather = this.chromaKey.feather;

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i+1];
      const b = data[i+2];

      let isBg = false;
      let opacity = 1.0; // Foreground opacity (1.0 = solid, 0.0 = transparent)

      // Use AI cutout if enabled and available
      if (this.useAICutout && maskData) {
        // MediaPipe mask contains alpha value in red channel (or alpha channel itself)
        const maskVal = maskData[i + 3] > 0 ? maskData[i] : 0; // 0 to 255
        opacity = maskVal / 255;
        if (opacity < 0.15) {
          isBg = true;
          opacity = 0;
        }
      } 
      // Else use Chroma Key if color selected
      else if (chromaRGB) {
        const dist = Math.sqrt(
          Math.pow(r - chromaRGB[0], 2) +
          Math.pow(g - chromaRGB[1], 2) +
          Math.pow(b - chromaRGB[2], 2)
        );

        if (dist < chromaTol) {
          isBg = true;
          opacity = 0.0;
        } else if (dist < chromaTol + chromaFeather * 10) {
          // Linear transition for feathering
          const delta = dist - chromaTol;
          opacity = delta / (chromaFeather * 10);
        }
      }

      // If it is background, let's keep it transparent for now (we'll draw new background color underneath)
      if (isBg || opacity < 1.0) {
        data[i + 3] = Math.round(opacity * 255);
      }
    }

    // Write transparent-background portrait image data back
    outCtx.putImageData(imgData, 0, 0);

    // Apply Image Filters (Brightness, Contrast, Saturation, Sharpen, Smooth)
    this.applyImageFilters(outCanvas);

    // Create final canvas with selected background
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = targetW;
    finalCanvas.height = targetH;
    const finalCtx = finalCanvas.getContext('2d');
    if (!finalCtx) return outCanvas;

    // Draw background fill
    if (this.bgType === 'solid') {
      finalCtx.fillStyle = this.bgSolidColor;
      finalCtx.fillRect(0, 0, targetW, targetH);
      
      // Vignette effect
      if (this.bgGradientVignette) {
        const grad = finalCtx.createRadialGradient(
          targetW / 2, targetH / 2, targetW * 0.1,
          targetW / 2, targetH / 2, targetH * 0.8
        );
        grad.addColorStop(0, 'rgba(255,255,255,0.25)');
        grad.addColorStop(1, 'rgba(0,0,0,0.25)');
        finalCtx.fillStyle = grad;
        finalCtx.fillRect(0, 0, targetW, targetH);
      }
    } else if (this.bgType === 'transparent') {
      // Keep transparent
    }

    // Draw the transparent-background photo on top of the solid background
    finalCtx.drawImage(outCanvas, 0, 0);

    return finalCanvas;
  }

  /**
   * Applies image beautification, filters, and convolutions directly on the canvas image data.
   */
  private applyImageFilters(canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = canvas.width;
    const h = canvas.height;
    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;

    // Filters sliders variables
    const bMul = this.filters.brightness / 100;
    const cVal = (this.filters.contrast - 100) / 100;
    const sMul = this.filters.saturation / 100;

    // Contrast modifier factor
    const factor = (259 * (cVal * 255 + 255)) / (255 * (259 - cVal * 255));

    // Skin smoothing approximation (simple bilateral-like selective blur)
    if (this.filters.smooth > 0) {
      this.smoothSkin(data, w, h, this.filters.smooth);
    }

    // Process basic color filters (Brightness, Contrast, Saturation, Grayscale)
    for (let i = 0; i < data.length; i += 4) {
      if (data[i + 3] === 0) continue; // skip fully transparent pixels

      let r = data[i];
      let g = data[i+1];
      let b = data[i+2];

      // 1. Brightness
      r *= bMul;
      g *= bMul;
      b *= bMul;

      // 2. Contrast
      r = factor * (r - 128) + 128;
      g = factor * (g - 128) + 128;
      b = factor * (b - 128) + 128;

      // 3. Saturation (standard RGB weightings: 0.299, 0.587, 0.114)
      const gray = 0.299 * r + 0.587 * g + 0.114 * b;
      r = gray + (r - gray) * sMul;
      g = gray + (g - gray) * sMul;
      b = gray + (b - gray) * sMul;

      // 4. Grayscale
      if (this.filters.grayscale) {
        r = gray;
        g = gray;
        b = gray;
      }

      // Clamp values
      data[i] = Math.max(0, Math.min(255, r));
      data[i+1] = Math.max(0, Math.min(255, g));
      data[i+2] = Math.max(0, Math.min(255, b));
    }

    ctx.putImageData(imgData, 0, 0);

    // 5. Sharpen Filter (Convolution Kernel)
    if (this.filters.sharpen > 0) {
      this.convolveSharpen(canvas, this.filters.sharpen);
    }
  }

  /**
   * Applies selective skin smoothing to face regions.
   * Leverages custom skin-color heuristics to blur only skin areas while retaining detail in eyes, hair, and outlines.
   */
  private smoothSkin(data: Uint8ClampedArray, w: number, h: number, intensity: number) {
    const original = new Uint8ClampedArray(data);
    const radius = Math.round(intensity * 1.5); // smoothing radius
    if (radius < 1) return;

    // Skin Color Heuristics (RGB bounds)
    const isSkin = (r: number, g: number, b: number) => {
      return r > 95 && g > 40 && b > 20 &&
             (Math.max(r, g, b) - Math.min(r, g, b) > 15) &&
             Math.abs(r - g) > 15 && r > g && r > b;
    };

    // Calculate selective box blur
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = (y * w + x) * 4;
        
        // If background (transparent), skip
        if (data[idx + 3] === 0) continue;

        const r = original[idx];
        const g = original[idx + 1];
        const b = original[idx + 2];

        // Only blur if skin color is detected
        if (isSkin(r, g, b)) {
          let sumR = 0, sumG = 0, sumB = 0, count = 0;

          // Average pixels in neighborhood
          for (let ky = -radius; ky <= radius; ky++) {
            const ny = y + ky;
            if (ny < 0 || ny >= h) continue;

            for (let kx = -radius; kx <= radius; kx++) {
              const nx = x + kx;
              if (nx < 0 || nx >= w) continue;

              const nIdx = (ny * w + nx) * 4;
              const nr = original[nIdx];
              const ng = original[nIdx + 1];
              const nb = original[nIdx + 2];

              // Only include in averaging if the neighbor is also skin
              if (isSkin(nr, ng, nb)) {
                sumR += nr;
                sumG += ng;
                sumB += nb;
                count++;
              }
            }
          }

          if (count > 0) {
            // Blend smoothed value with original based on intensity
            const blend = intensity / 10; // 0.0 to 1.0
            data[idx] = Math.round(r * (1 - blend) + (sumR / count) * blend);
            data[idx + 1] = Math.round(g * (1 - blend) + (sumG / count) * blend);
            data[idx + 2] = Math.round(b * (1 - blend) + (sumB / count) * blend);
          }
        }
      }
    }
  }

  /**
   * Applies a convolution sharpen kernel to the canvas.
   */
  private convolveSharpen(canvas: HTMLCanvasElement, amount: number) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const w = canvas.width;
    const h = canvas.height;
    const imgData = ctx.getImageData(0, 0, w, h);
    const data = imgData.data;
    const output = ctx.createImageData(w, h);
    const outData = output.data;

    // Sharpen kernel:
    //  0  -1   0
    // -1   5  -1
    //  0  -1   0
    // We adjust the weight based on amount
    const kCenter = 1 + 4 * (amount / 5);
    const kEdge = -(amount / 5);

    const kernel = [
       0,    kEdge,  0,
       kEdge, kCenter, kEdge,
       0,    kEdge,  0
    ];

    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        let r = 0, g = 0, b = 0;
        
        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = ((y + ky) * w + (x + kx)) * 4;
            const kVal = kernel[(ky + 1) * 3 + (kx + 1)];
            
            r += data[idx] * kVal;
            g += data[idx + 1] * kVal;
            b += data[idx + 2] * kVal;
          }
        }

        const oIdx = (y * w + x) * 4;
        outData[oIdx] = Math.max(0, Math.min(255, r));
        outData[oIdx + 1] = Math.max(0, Math.min(255, g));
        outData[oIdx + 2] = Math.max(0, Math.min(255, b));
        outData[oIdx + 3] = data[oIdx + 3]; // Keep original alpha
      }
    }

    // Copy edge pixels unchanged
    for (let x = 0; x < w; x++) {
      const top = x * 4;
      const bottom = ((h - 1) * w + x) * 4;
      for (let c = 0; c < 4; c++) {
        outData[top + c] = data[top + c];
        outData[bottom + c] = data[bottom + c];
      }
    }
    for (let y = 0; y < h; y++) {
      const left = (y * w) * 4;
      const right = (y * w + (w - 1)) * 4;
      for (let c = 0; c < 4; c++) {
        outData[left + c] = data[left + c];
        outData[right + c] = data[right + c];
      }
    }

    ctx.putImageData(output, 0, 0);
  }

  // Interactive Events setup
  private setupEventListeners() {
    // Mouse events
    this.canvas.addEventListener('mousedown', (e) => this.handleDragStart(e.clientX, e.clientY));
    window.addEventListener('mousemove', (e) => this.handleDragMove(e.clientX, e.clientY));
    window.addEventListener('mouseup', () => this.handleDragEnd());

    // Touch events for mobile
    this.canvas.addEventListener('touchstart', (e) => {
      if (e.touches.length === 1) {
        this.handleDragStart(e.touches[0].clientX, e.touches[0].clientY);
      }
    });
    window.addEventListener('touchmove', (e) => {
      if (e.touches.length === 1 && this.isGrabbing) {
        this.handleDragMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    });
    window.addEventListener('touchend', () => this.handleDragEnd());

    // Wheel zooming
    this.canvas.addEventListener('wheel', (e) => {
      if (!this.sourceImage || !this.isEditMode) return;
      e.preventDefault();
      
      const zoomFactor = e.deltaY < 0 ? 1.05 : 0.95;
      this.cropState.scale = Math.max(0.2, Math.min(10.0, this.cropState.scale * zoomFactor));
      this.update();
    }, { passive: false });
  }

  private handleDragStart(clientX: number, clientY: number) {
    if (!this.sourceImage || !this.isEditMode) return;
    this.isGrabbing = true;
    this.canvas.classList.add('grabbing');
    this.startDragX = clientX;
    this.startDragY = clientY;
  }

  private handleDragMove(clientX: number, clientY: number) {
    if (!this.isGrabbing || !this.sourceImage) return;

    const dx = clientX - this.startDragX;
    const dy = clientY - this.startDragY;
    
    this.startDragX = clientX;
    this.startDragY = clientY;

    // Calculate crop window size
    const margin = 50;
    const aspect = this.targetSize.widthMm / this.targetSize.heightMm;
    const maxCropW = this.canvas.width - margin * 2;
    const maxCropH = this.canvas.height - margin * 2;
    
    let cropW = maxCropW;
    let cropH = cropW / aspect;
    if (cropH > maxCropH) {
      cropH = maxCropH;
      cropW = cropH * aspect;
    }

    // Shift offsets normalized by crop window dimensions
    // When zoomed visual scale, dragging coordinates matches screen pixels.
    // So shift = screenDelta / (cropSize * visualViewportZoomLevel)
    this.cropState.x += dx / (cropW * this.zoomLevel);
    this.cropState.y += dy / (cropH * this.zoomLevel);
    this.update();
  }

  private handleDragEnd() {
    if (this.isGrabbing) {
      this.isGrabbing = false;
      this.canvas.classList.remove('grabbing');
    }
  }
}
